<!-- Filters -->
<div class="mb-4 flex items-center gap-2 flex-wrap">
  <div class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg">
    <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
    </svg>
    <select [(ngModel)]="filterEmployeeId" class="text-xs bg-transparent border-none outline-none cursor-pointer text-gray-600 pr-4">
      <option value="">All Members</option>
      @for(emp of visibleEmployees; track emp.id) {<option [value]="emp.id">{{ emp.firstName }} {{ emp.name }}{{ emp.isArchived ? ' (archived)' : '' }}</option>}
    </select>
    @if(archivedEmployeeCount > 0) {
      <label class="flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap pl-1.5 border-l border-gray-200"
             title="Archived members are hidden by default">
        <input type="checkbox" [(ngModel)]="showArchivedEmployees"
               class="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer">
        Archived ({{ archivedEmployeeCount }})
      </label>
    }
  </div>
  <div class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg">
    <select [(ngModel)]="filterStatus" class="text-xs bg-transparent border-none outline-none cursor-pointer text-gray-600 pr-4">
      <option value="">All Status</option>
      <option value="NOTBEGIN">New</option>
      <option value="INPROGRESS">In Progress</option>
      <option value="DONE">Done</option>
    </select>
  </div>
  <div class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg">
    <select [(ngModel)]="filterPriority" class="text-xs bg-transparent border-none outline-none cursor-pointer text-gray-600 pr-4">
      <option value="">All Priorities</option>
      <option value="HIGH">High</option>
      <option value="MEDIUM">Medium</option>
      <option value="LOW">Low</option>
    </select>
  </div>
</div>

<!-- Kanban Board -->
<div cdkDropListGroup class="flex gap-4 overflow-x-auto pb-4">
  @for(stage of projectStages; track stage.projectStageId) {
  @let stageTaskCount = tasksByStage[stage.projectStageId!]?.length || 0;

  <div class="min-w-[280px] max-w-[320px] flex flex-col shrink-0 rounded-xl bg-gray-50 border border-gray-200 overflow-hidden">

    <!-- Column Header -->
    <div class="px-4 py-3 border-b border-gray-200" [style.border-top]="'3px solid ' + stage.color">
      <div class="flex items-center justify-between mb-1.5">
        <div class="flex items-center gap-2 min-w-0">
          <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="stage.color"></span>
          <h3 class="text-sm font-bold text-gray-800 truncate">{{ stage.stageName }}</h3>
          <span class="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-[10px] font-bold text-gray-600">
            {{ stageTaskCount }}
          </span>
        </div>
        <button (click)="onAddTask(stage.projectStageId!)"
          class="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer shrink-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
        </button>
      </div>
      @let stageEncoded = getStageEncodedHours(stage.projectStageId!);
      @let stageBudget = stage.estimatedHours || 0;
      @if(stageBudget > 0 || stageEncoded > 0) {
      <span class="text-[10px] font-medium"
        [ngClass]="stageBudget > 0 && stageEncoded > stageBudget ? 'text-red-500' : 'text-gray-400'">
        {{ stageEncoded | number:'1.0-1' }}h<!--
        -->@if(stageBudget > 0) {<span class="text-gray-300"> / </span>{{ stageBudget }}h}
      </span>
      }
    </div>

    <!-- Task Cards -->
    <div cdkDropList
      [cdkDropListData]="tasksByStage[stage.projectStageId!] || []"
      (cdkDropListDropped)="onDrop($event, stage.projectStageId!)"
      [cdkDropListConnectedTo]="stageIds"
      class="flex flex-col gap-2 p-3 flex-1 min-h-[60px] overflow-y-auto">

      @for(task of tasksByStage[stage.projectStageId!]; track task.id) {
      @if(filterTask(task)) {
      @let assignedEmployees = getTaskEmployees(task);

      <!-- cdkDragData carries the task itself, so the drop handler never has to
           map a rendered index back onto the unfiltered column data. The delay is
           kept for touch (where it separates a drag from a tap) but dropped for
           the mouse, where it only made the board feel unresponsive. -->
      <div cdkDrag [cdkDragData]="task" [cdkDragStartDelay]="{ touch: 200, mouse: 0 }"
        [cdkDragDisabled]="task.status === 'DONE'"
        (cdkDragStarted)="onDragStarted(task)" (cdkDragEnded)="onDragEnded()"
        (click)="openDetailTask(task.id!)"
        class="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all group">

        <!-- Priority indicator + Name -->
        <div class="flex items-start gap-2 mb-2">
          <span class="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5"
            [ngClass]="{'bg-red-500': task.priority === 'HIGH', 'bg-amber-400': task.priority === 'MEDIUM', 'bg-gray-300': task.priority === 'LOW' || !task.priority}">
          </span>
          <span class="text-sm font-medium text-gray-800 leading-snug line-clamp-2">{{ task.nameTask }}</span>
        </div>

        <!-- Bottom row: assignees + meta -->
        <div class="flex items-center justify-between gap-2">
          <!-- Assignees -->
          <div class="flex items-center -space-x-1">
            @for(emp of assignedEmployees.slice(0,3); track emp.id) {
            <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[8px] font-bold bg-indigo-100 text-indigo-700 border border-white"
              title="{{ emp.firstName }} {{ emp.name }}">
              {{ getInitials(emp) }}
            </span>
            }
            @if(assignedEmployees.length > 3) {
            <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[8px] font-bold bg-gray-100 text-gray-500 border border-white">
              +{{ assignedEmployees.length - 3 }}
            </span>
            }
          </div>

          <div class="flex items-center gap-1.5">
            <!-- Hours -->
            @if(task.estimatedHours) {
            <span class="text-[10px] font-semibold text-gray-400">{{ task.estimatedHours }}h</span>
            }
            <!-- Status pill -->
            <span class="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider"
              [ngClass]="{
                'bg-gray-100 text-gray-500': task.status === 'NOTBEGIN',
                'bg-blue-100 text-blue-600': task.status === 'INPROGRESS',
                'bg-green-100 text-green-600': task.status === 'DONE'}">
              {{ task.status === 'NOTBEGIN' ? 'New' : task.status === 'INPROGRESS' ? 'Active' : 'Done' }}
            </span>
          </div>
        </div>
      </div>
      }
      }

      <!-- Empty state -->
      @if(stageTaskCount === 0) {
      <div class="flex flex-col items-center justify-center py-6 text-center">
        <svg class="w-8 h-8 text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
        </svg>
        <p class="text-[11px] text-gray-400">No tasks yet</p>
      </div>
      }
    </div>
  </div>
  }
</div>


